import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { boardApi, ListBoardsParams } from '../services/taskApi';
import { BoardCreate, BoardUpdate, BoardMemberRole } from '../types';
import { workspaceKeys } from './useWorkspaces';

export const BOARDS_QUERY_KEY = 'boards';

interface UseBoardsOptions extends ListBoardsParams {
  enabled?: boolean;
}

export function useBoards(options: UseBoardsOptions = {}) {
  const { enabled = true, ...params } = options;
  return useQuery({
    queryKey: [BOARDS_QUERY_KEY, params],
    queryFn: () => boardApi.list(params),
    enabled,
    staleTime: 10_000,
  });
}

/**
 * Fetch boards shared with the user where they don't have workspace access.
 * These are "orphaned" shared boards - useful for showing in a "Shared with me" section.
 */
export function useSharedBoards(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: [BOARDS_QUERY_KEY, 'shared'],
    queryFn: () => boardApi.listShared(),
    enabled,
    staleTime: 10_000,
  });
}

export function useBoard(
  id: string,
  options?: { includeGroups?: boolean; includeMembers?: boolean }
) {
  return useQuery({
    queryKey: [BOARDS_QUERY_KEY, id, options],
    queryFn: () => boardApi.get(id, options),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useCreateBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BoardCreate) => boardApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOARDS_QUERY_KEY] });
      // Invalidate workspace details since they include boards
      queryClient.invalidateQueries({ queryKey: workspaceKeys.details() });
    },
  });
}

export function useUpdateBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BoardUpdate }) =>
      boardApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOARDS_QUERY_KEY] });
      // Invalidate workspace details since they include boards
      queryClient.invalidateQueries({ queryKey: workspaceKeys.details() });
    },
  });
}

export function useDeleteBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, deleteItems }: { id: string; deleteItems?: boolean }) =>
      boardApi.delete(id, { deleteItems }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOARDS_QUERY_KEY] });
      // Invalidate workspace details since they include boards
      queryClient.invalidateQueries({ queryKey: workspaceKeys.details() });
    },
  });
}

/** Preview what deleting a board will affect (shared DeletionImpact — the
 * delete_tasks keep/remove option + the default-board blocker). Fetched when the
 * delete dialog opens. */
export function useBoardDeletionImpact(id: string | null) {
  return useQuery({
    queryKey: [BOARDS_QUERY_KEY, 'deletion-impact', id],
    queryFn: () => boardApi.getDeletionImpact(id!),
    enabled: !!id,
  });
}

export function useAddBoardMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      boardId,
      userId,
      role,
    }: {
      boardId: string;
      userId: string;
      role: BoardMemberRole;
    }) => boardApi.addMember(boardId, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOARDS_QUERY_KEY] });
    },
  });
}

export function useRemoveBoardMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boardId, userId }: { boardId: string; userId: string }) =>
      boardApi.removeMember(boardId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOARDS_QUERY_KEY] });
    },
  });
}

export function useReorderBoards() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      boardIds,
    }: {
      workspaceId: string | null;
      boardIds: string[];
    }) => boardApi.reorder(workspaceId, boardIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOARDS_QUERY_KEY] });
      // Invalidate workspace details since they include boards
      queryClient.invalidateQueries({ queryKey: workspaceKeys.details() });
    },
  });
}
